import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import {
  AlertCircle, Archive, BookOpen, Check, Clipboard, Download, Film, Image as ImageIcon, Layers3, Loader2,
  Maximize2, Music2, Pencil, Play, Plus, Save, Search, Sparkles, Users, UserRound,
} from "lucide-react";
import {
  archiveLibraryCategory, archiveLibraryItem, buildDownloadFilename, createLibraryCategory,
  getLibraryMediaUrl, libraryItemsPath, swrFetcher,
  updateLibraryCategory, updateLibraryItem,
  type AssetType, type LibraryCategoryInfo, type LibraryItemInfo,
  type LibraryItemsResponse, type LibraryScope,
} from "@/lib/api";
import { formatTimestamp } from "@/lib/format";
import { aspectRatioForJobAsset } from "@/lib/aspect";
import { useLibraryMediaUrl } from "@/hooks/useLibraryMediaUrl";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { MediaLightbox } from "@/components/MediaLightbox";
import { cn } from "@/lib/utils";
import { useDownloadAsset } from "@/hooks/useDownloadAsset";
import { EmptyState } from "@/components/ui/empty-state";
import { TechniqueGuide } from "@/components/LibraryTechniqueGuide";
import { AssetMedia } from "@/components/media/AssetMedia";

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

type MediaFilter = "all" | AssetType;
type ScopeFilter = "all" | LibraryScope;

const MEDIA_OPTIONS = [
  { value: "all" as const, label: "All" },
  { value: "image" as const, label: "Images" },
  { value: "video" as const, label: "Videos" },
  { value: "audio" as const, label: "Audio" },
];

const SCOPE_OPTIONS = [
  { value: "all" as const, label: "All scopes" },
  { value: "system" as const, label: "System" },
  { value: "team" as const, label: "Team" },
  { value: "personal" as const, label: "Personal" },
];

const WORKFLOWS = [
  ["all", "All workflows"], ["prompt_template", "Prompt templates"], ["text_to_image", "Text to Image"],
  ["image_to_image", "Image to Image"], ["text_to_video", "Text to Video"],
  ["image_to_video", "Image to Video"], ["reference_to_video", "Reference to Video"],
  ["text_to_speech", "Text to Speech"], ["creative_direction", "Creative Direction"],
] as const;

const workflowLabel = (key: string) =>
  WORKFLOWS.find(([value]) => value === key)?.[1] ?? key.replaceAll("_", " ");

const ScopeIcon = ({ scope }: { scope: LibraryScope }) =>
  scope === "system" ? <Sparkles className="h-3 w-3" />
    : scope === "team" ? <Users className="h-3 w-3" />
      : <UserRound className="h-3 w-3" />;

function LibraryCard({ item, onOpen }: { item: LibraryItemInfo; onOpen: () => void }) {
  const primary = item.media_assets.find((entry) => entry.role === "primary");
  const { media, isLoading } = useLibraryMediaUrl(item.id, primary?.id);
  const [copied, setCopied] = useState(false);
  const isCreativeDirection = Boolean(item.application);

  async function copyPrompt() {
    await navigator.clipboard.writeText(item.prompt);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  }

  return (
    <div data-testid="library-card" data-library-id={item.id} data-library-scope={item.scope} className="hover-lift group overflow-hidden rounded-xl border border-studio-border bg-studio-panel shadow-panel transition-[border-color,transform] hover:-translate-y-0.5 hover:border-studio-border-light sm:rounded-2xl">
      <button type="button" onClick={onOpen} className="relative block aspect-video w-full overflow-hidden bg-studio-bg text-left">
        {media?.url ? (
          item.media_type === "video" ? (
            media.poster_url ? <img src={media.poster_url} alt={item.title} loading="lazy" className="h-full w-full object-cover" /> : <Film className="m-auto h-8 w-8 text-studio-muted" />
          ) : item.media_type === "image" ? (
            <img src={media.poster_url ?? media.url} loading="lazy" alt={item.title} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center bg-gradient-to-br from-studio-card to-studio-bg"><Music2 className="h-10 w-10 text-studio-accent" /></div>
          )
        ) : (
          <div className={cn("flex h-full items-center justify-center", isCreativeDirection && "bg-gradient-to-br from-studio-accent-soft via-studio-card to-studio-panel")}>
            {isLoading ? <Loader2 className="h-6 w-6 animate-spin text-studio-muted" /> : isCreativeDirection ? <Sparkles className="h-7 w-7 text-studio-accent" /> : <Film className="h-7 w-7 text-studio-faint" />}
          </div>
        )}
        {item.media_type === "video" && <span className="absolute bottom-3 left-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur"><Play className="h-3.5 w-3.5 fill-current" /></span>}
        <Badge variant={item.scope === "system" ? "accent" : "neutral"} className="absolute right-3 top-3 bg-studio-panel/90 backdrop-blur">
          <ScopeIcon scope={item.scope} /> {item.scope}
        </Badge>
      </button>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold text-studio-ink">{item.title}</h2>
            <p className="mt-1 text-[11px] font-medium text-studio-muted">{workflowLabel(item.workflow_key)}</p>
          </div>
          <span className="rounded-lg bg-studio-card p-2 text-studio-muted">
            {item.media_type === "video" ? <Film className="h-4 w-4" /> : item.media_type === "image" ? <ImageIcon className="h-4 w-4" /> : <Music2 className="h-4 w-4" />}
          </span>
        </div>
        <p className="mt-3 line-clamp-3 min-h-[3.75rem] text-xs leading-5 text-studio-muted">{item.prompt}</p>
        <div className="mt-3 flex min-h-5 flex-wrap gap-1.5">
          {item.categories.slice(0, 3).map((category) => <Badge key={category.id}>{category.name}</Badge>)}
          {item.categories.length > 3 && <Badge>+{item.categories.length - 3}</Badge>}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button variant="secondary" size="sm" onClick={() => void copyPrompt()}>
            {copied ? <Check className="h-3.5 w-3.5 text-studio-success" /> : <Clipboard className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy prompt"}
          </Button>
          <Button size="sm" onClick={onOpen}><BookOpen className="h-3.5 w-3.5" /> View technique</Button>
        </div>
      </div>
    </div>
  );
}

function ItemDetail({
  item,
  categories,
  onClose,
  onUpdated,
  onArchived,
}: {
  item: LibraryItemInfo | null;
  categories: LibraryCategoryInfo[];
  onClose: () => void;
  onUpdated: (item: LibraryItemInfo) => void;
  onArchived: (item: LibraryItemInfo) => void;
}) {
  const navigate = useNavigate();
  const [selection, setSelection] = useState<{ itemId: string; mediaId: string } | null>(null);
  const activeMedia = item?.media_assets.find((entry) => entry.id === (selection?.itemId === item.id ? selection.mediaId : null)) ?? item?.media_assets.find((entry) => entry.role === "primary");
  const { media } = useLibraryMediaUrl(item?.id, activeMedia?.id);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const { download, downloading, failed: downloadError, reset: resetDownload } = useDownloadAsset();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEditing(false);
    setTitle(item?.title ?? "");
    setPrompt(item?.prompt ?? "");
    setCategoryIds(item?.categories.map((category) => category.id) ?? []);
    setError(null);
    setLightboxOpen(false);
    resetDownload();
  }, [item, resetDownload]);

  if (!item) return null;
  const itemId = item.id;
  const editableCategories = categories.filter((category) =>
    !category.archived && (
      item.scope === "personal" ||
      (item.scope === "team" && category.scope !== "personal") ||
      (item.scope === "system" && category.scope === "system")
    )
  );
  const recipeParams = asRecord(asRecord(item.recipe).params);
  const detailAspectRatio = activeMedia?.metadata.width && activeMedia.metadata.height ? `${activeMedia.metadata.width} / ${activeMedia.metadata.height}` : aspectRatioForJobAsset({ metadata: item.source_metadata }, recipeParams);
  const canExpand = !!media?.url && activeMedia?.media_type !== "audio";

  async function saveEdit() {
    if (!title.trim() || !prompt.trim()) return;
    setSaving(true); setError(null);
    try {
      const updated = await updateLibraryItem(itemId, {
        title: title.trim(), prompt: prompt.trim(), category_ids: categoryIds,
      });
      onUpdated(updated); setEditing(false);
    } catch (err) { setError(err instanceof Error ? err.message : "Library item could not be updated"); }
    finally { setSaving(false); }
  }

  async function archiveItem() {
    setSaving(true); setError(null);
    try { onArchived(await archiveLibraryItem(itemId)); }
    catch (err) { setError(err instanceof Error ? err.message : "Library item could not be archived"); }
    finally { setSaving(false); }
  }

  async function handleDownload() {
    if (!item || !activeMedia || downloading) return;
    const filename = buildDownloadFilename(item.workflow_key, itemId, 0, activeMedia.mime);
    await download({
      filename,
      resolveUrl: async () => (await getLibraryMediaUrl(itemId, activeMedia.id, { download: true, filename })).url,
    });
  }

  return (
    <Dialog open onClose={onClose} title={item.title} className="max-w-3xl">
      <div className="overflow-y-auto p-4 sm:p-5">
        <div
          className={cn("relative overflow-hidden rounded-xl border border-studio-border bg-studio-bg", !detailAspectRatio && "aspect-video")}
          style={detailAspectRatio ? { aspectRatio: detailAspectRatio } : undefined}
        >
          {!activeMedia && <div className="flex h-full items-center justify-center bg-gradient-to-br from-studio-accent-soft via-studio-card to-studio-panel"><Sparkles className="h-10 w-10 text-studio-accent" /></div>}
          {media?.url && <AssetMedia type={activeMedia?.media_type ?? item.media_type} url={media.url} poster={media.poster_url} alt={item.title} variant="theater" className="bg-transparent" />}
          {canExpand && (
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              aria-label="Expand media"
              title="Expand"
              className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-lg bg-black/60 text-white backdrop-blur transition-colors hover:bg-black/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          )}
        </div>
        {activeMedia?.metadata.width && <p className="mt-2 text-xs text-studio-muted">{activeMedia.metadata.width} × {activeMedia.metadata.height}{activeMedia.metadata.duration_sec ? ` · ${activeMedia.metadata.duration_sec.toFixed(1)} seconds` : ""}</p>}
        <div className="mt-3 flex flex-wrap gap-2">{item.media_assets.map((entry) => <Button key={entry.id} variant={activeMedia?.id === entry.id ? "primary" : "secondary"} size="sm" onClick={() => { setSelection({ itemId: item.id, mediaId: entry.id }); setLightboxOpen(false); resetDownload(); }}>{entry.media_type === "image" ? "Original image" : "Video"}</Button>)}</div>
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <Badge variant="accent"><ScopeIcon scope={item.scope} /> {item.scope}</Badge>
          <Badge>{workflowLabel(item.workflow_key)}</Badge>
          {item.categories.map((category) => <Badge key={category.id}>{category.name}</Badge>)}
        </div>
        <div className="mt-5 rounded-xl border border-studio-border bg-studio-card p-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-studio-muted">{editing ? "Edit item" : "Prompt"}</p>
            {!editing && <Button variant="ghost" size="sm" onClick={() => void navigator.clipboard.writeText(item.prompt).then(() => setCopied(true))}>
              {copied ? <Check className="h-3.5 w-3.5" /> : <Clipboard className="h-3.5 w-3.5" />} {copied ? "Copied" : "Copy"}
            </Button>}
          </div>
          {editing ? <div className="mt-3 space-y-3">
            <Input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={180} />
            <textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} rows={6} className="w-full resize-y rounded-lg border border-studio-border bg-studio-panel px-3 py-2 text-sm text-studio-ink outline-none focus:border-studio-accent" />
            <div className="flex max-h-32 flex-wrap gap-2 overflow-y-auto">
              {editableCategories.map((category) => <button key={category.id} type="button" onClick={() => setCategoryIds((current) => current.includes(category.id) ? current.filter((id) => id !== category.id) : [...current, category.id])} className={cn("rounded-full border px-2.5 py-1 text-[11px]", categoryIds.includes(category.id) ? "border-studio-accent bg-studio-accent text-studio-accent-fg" : "border-studio-border text-studio-muted")}>{categoryIds.includes(category.id) && <Check className="mr-1 inline h-3 w-3" />}{category.name}</button>)}
            </div>
          </div> : <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-studio-ink">{item.prompt}</p>}
        </div>
        <div className="mt-4 grid gap-3 text-xs text-studio-muted sm:grid-cols-2">
          {item.source_url && <p>Source: <a href={item.source_url} target="_blank" rel="noreferrer" className="text-studio-accent hover:underline">{item.source ?? item.source_url}</a></p>}
          <p>Added: {formatTimestamp(item.created_at)}</p>
        </div>
        <TechniqueGuide item={item} />
        <details className="mt-4 rounded-xl border border-studio-border bg-studio-card px-4 py-3 text-xs text-studio-muted">
          <summary className="cursor-pointer font-semibold text-studio-ink">Production recipe and provenance</summary>
          <pre className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-[10px] leading-5">{JSON.stringify({ recipe: item.recipe, source: item.source_metadata }, null, 2)}</pre>
        </details>
        {error && <p className="mt-3 rounded-lg bg-studio-danger/10 px-3 py-2 text-xs text-studio-danger">{error}</p>}
      </div>
      <div className="grid grid-cols-1 gap-2 border-t border-studio-border px-4 py-4 sm:flex sm:flex-wrap sm:justify-end sm:px-5">
        {item.can_manage && !editing && <Button variant="ghost" onClick={() => void archiveItem()} loading={saving}><Archive className="h-4 w-4" /> Archive</Button>}
        {activeMedia && !editing && (
          <Button variant={downloadError ? "danger" : "secondary"} onClick={() => void handleDownload()} loading={downloading}>
            {downloadError ? <AlertCircle className="h-4 w-4" /> : <Download className="h-4 w-4" />}
            {downloadError ? "Download failed — retry" : "Download"}
          </Button>
        )}
        <span role="status" aria-live="polite" className="sr-only">{downloadError ? "Download failed. Please try again." : ""}</span>
        {item.can_manage && (editing ? <>
          <Button variant="secondary" onClick={() => setEditing(false)}>Cancel edit</Button>
          <Button onClick={() => void saveEdit()} loading={saving} disabled={!title.trim() || !prompt.trim()}><Check className="h-4 w-4" /> Save changes</Button>
        </> : <Button variant="secondary" onClick={() => setEditing(true)}><Pencil className="h-4 w-4" /> Edit</Button>)}
        {!editing && <Button variant="secondary" onClick={onClose}>Close</Button>}
        {!editing && item.application && <Button variant="secondary" onClick={() => navigate(`/?creative_direction=1&direction_item=${item.id}${item.application?.modalities?.length === 1 ? `&direction_modality=${item.application.modalities[0]}` : ""}`)}>Choose Creative Direction</Button>}
        {!editing && item.scope === "system" && item.supported_targets.map((target) => <Button key={target} onClick={() => navigate(`/?library_item=${item.id}&library_target=${target}`)}>Use prompt for {target}</Button>)}
        {!editing && item.scope !== "system" && <Button onClick={() => navigate(`/?library_item=${item.id}`)}>Use in Studio</Button>}
      </div>
      <MediaLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        url={media?.url ?? null}
        type={activeMedia?.media_type ?? item.media_type}
        posterUrl={media?.poster_url}
        alt={item.title}
      />
    </Dialog>
  );
}

function ManageCategoriesDialog({
  open,
  categories,
  onClose,
  onChanged,
}: {
  open: boolean;
  categories: LibraryCategoryInfo[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const [name, setName] = useState("");
  const [scope, setScope] = useState<"personal" | "team">("personal");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingNames, setEditingNames] = useState<Record<string, string>>({});
  const manageable = categories.filter((category) => category.can_manage && !category.archived);

  useEffect(() => {
    if (!open) return;
    setEditingNames(Object.fromEntries(
      categories.filter((category) => category.can_manage && !category.archived)
        .map((category) => [category.id, category.name])
    ));
    setError(null);
  }, [open, categories]);

  async function save() {
    if (!name.trim()) return;
    setSaving(true); setError(null);
    try {
      await createLibraryCategory({ name: name.trim(), scope });
      setName(""); onChanged();
    } catch (err) { setError(err instanceof Error ? err.message : "Category could not be created"); }
    finally { setSaving(false); }
  }

  async function rename(category: LibraryCategoryInfo) {
    const nextName = editingNames[category.id]?.trim();
    if (!nextName || nextName === category.name) return;
    setSaving(true); setError(null);
    try { await updateLibraryCategory(category.id, { name: nextName }); onChanged(); }
    catch (err) { setError(err instanceof Error ? err.message : "Category could not be renamed"); }
    finally { setSaving(false); }
  }

  async function archive(category: LibraryCategoryInfo) {
    setSaving(true); setError(null);
    try { await archiveLibraryCategory(category.id); onChanged(); }
    catch (err) { setError(err instanceof Error ? err.message : "Category could not be archived"); }
    finally { setSaving(false); }
  }

  return <Dialog open={open} onClose={onClose} title="Manage Library categories">
    <div className="space-y-4 p-4 sm:p-5">
      <div><label className="mb-2 block text-xs font-semibold text-studio-muted">Name</label><Input value={name} onChange={(event) => setName(event.target.value)} placeholder="e.g. Realism" /></div>
      <div><label className="mb-2 block text-xs font-semibold text-studio-muted">Visibility</label><Select value={scope} onChange={(event) => setScope(event.target.value as "personal" | "team")}><option value="personal">Personal</option><option value="team">Team</option></Select></div>
      <Button className="w-full sm:w-auto" loading={saving} disabled={!name.trim()} onClick={() => void save()}><Plus className="h-4 w-4" /> Create category</Button>
      {manageable.length > 0 && <div className="border-t border-studio-border pt-4">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-studio-muted">Your manageable categories</p>
        <div className="max-h-56 space-y-2 overflow-y-auto">
          {manageable.map((category) => <div key={category.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-studio-border bg-studio-card p-2">
            <div className="min-w-0 basis-full sm:flex-1"><Input value={editingNames[category.id] ?? category.name} onChange={(event) => setEditingNames((current) => ({ ...current, [category.id]: event.target.value }))} /></div>
            <Badge>{category.scope}</Badge>
            <Button variant="ghost" size="icon" disabled={saving || !editingNames[category.id]?.trim() || editingNames[category.id] === category.name} onClick={() => void rename(category)} aria-label={`Rename ${category.name}`}><Save className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" disabled={saving} onClick={() => void archive(category)} aria-label={`Archive ${category.name}`}><Archive className="h-4 w-4" /></Button>
          </div>)}
        </div>
      </div>}
      {error && <p className="text-xs text-studio-danger">{error}</p>}
    </div>
    <div className="flex justify-end gap-2 border-t border-studio-border px-4 py-4 sm:px-5"><Button className="w-full sm:w-auto" variant="secondary" onClick={onClose}>Done</Button></div>
  </Dialog>;
}

export function LibraryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const relatedSource = searchParams.get("source");
  const relatedTechnique = searchParams.get("technique");
  const { data: related } = useSWR<LibraryItemsResponse>(relatedSource && relatedTechnique ? `/api/library/items?source=${encodeURIComponent(relatedSource)}&external_id=${encodeURIComponent(relatedTechnique)}` : null, swrFetcher);
  const [mediaType, setMediaType] = useState<MediaFilter>("all");
  const [scope, setScope] = useState<ScopeFilter>("all");
  const [workflow, setWorkflow] = useState("all");
  const [source, setSource] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [mood, setMood] = useState("");
  const [offset, setOffset] = useState(0);
  const { data: facets } = useSWR<{ sources: string[]; difficulties: string[]; moods: string[] }>("/api/library/facets", swrFetcher);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [sort, setSort] = useState<"newest" | "oldest" | "title">("newest");
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [selected, setSelected] = useState<LibraryItemInfo | null>(null);
  const [categoryDialog, setCategoryDialog] = useState(false);
  useEffect(() => { if (related?.items[0]) setSelected(related.items[0]); }, [related]);

  useEffect(() => { const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 220); return () => window.clearTimeout(timer); }, [query]);
  useEffect(() => setOffset(0), [debouncedQuery, mediaType, workflow, scope, categoryIds, sort, source, difficulty, mood]);
  const itemsKey = libraryItemsPath({
    q: debouncedQuery || undefined, media_type: mediaType === "all" ? undefined : mediaType,
    workflow_key: workflow === "all" ? undefined : workflow,
    scope, category_ids: categoryIds, sort, source, difficulty, mood, limit: 48, offset,
  });
  const { data, isLoading, mutate: mutateItems } = useSWR<LibraryItemsResponse>(itemsKey, swrFetcher);
  const { data: categories, mutate: mutateCategories } = useSWR<LibraryCategoryInfo[]>("/api/library/categories", swrFetcher);
  const { data: sourceCategories } = useSWR<LibraryCategoryInfo[]>(source ? `/api/library/categories?source=${encodeURIComponent(source)}` : null, swrFetcher);
  const visibleCategories = useMemo(() => ((source ? sourceCategories : categories) ?? []).filter((category) => category.item_count || category.scope !== "system"), [categories, sourceCategories, source]);
  function toggleCategory(id: string) { setCategoryIds((current) => current.includes(id) ? current.filter((value) => value !== id) : [...current, id]); }

  return <div className="page-container max-w-[1500px]">
    <div className="page-header items-start">
      <div><div className="flex items-center gap-2 text-studio-accent"><BookOpen className="h-5 w-5" /><span className="text-[10px] font-semibold uppercase tracking-[0.18em]">Reusable production knowledge</span></div><h1 className="mt-1 text-2xl font-semibold tracking-tight text-studio-ink">Library</h1><p className="mt-1 max-w-2xl text-sm text-studio-muted">Browse proven prompts and media references, then move a complete recipe into Studio.</p></div>
      <Button className="w-full sm:w-auto" variant="secondary" onClick={() => setCategoryDialog(true)}><Layers3 className="h-4 w-4" /> Manage categories</Button>
    </div>

    <div className="rounded-xl border border-studio-border bg-studio-panel p-3 shadow-panel sm:rounded-2xl sm:p-4">
      <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <SegmentedControl options={MEDIA_OPTIONS} value={mediaType} onChange={setMediaType} className="w-full sm:w-auto" />
        <SegmentedControl options={SCOPE_OPTIONS} value={scope} onChange={setScope} className="w-full sm:w-auto" />
        <div className="relative min-w-0 flex-1 sm:min-w-[220px]"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-studio-faint" /><Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search titles, descriptions and prompts" className="pl-9" /></div>
        <div className="w-full sm:w-auto"><Select value={sort} onChange={(event) => setSort(event.target.value as typeof sort)} className="sm:min-w-[130px]"><option value="newest">Newest</option><option value="oldest">Oldest</option><option value="title">Title A–Z</option></Select></div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2 border-t border-studio-border pt-4">
        {WORKFLOWS.map(([value, label]) => <button key={value} type="button" onClick={() => setWorkflow(value)} className={cn("min-h-11 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors sm:min-h-8", workflow === value ? "border-studio-accent bg-studio-accent-soft text-studio-accent" : "border-studio-border text-studio-muted hover:text-studio-ink")}>{label}</button>)}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Select aria-label="Source" value={source} onChange={(event) => { setSource(event.target.value); setCategoryIds([]); }}><option value="">All sources</option>{facets?.sources.map((value) => <option key={value}>{value}</option>)}</Select>
        <Select aria-label="Difficulty" value={difficulty} onChange={(event) => setDifficulty(event.target.value)}><option value="">All difficulties</option>{facets?.difficulties.map((value) => <option key={value}>{value}</option>)}</Select>
        <Select aria-label="Mood" value={mood} onChange={(event) => setMood(event.target.value)}><option value="">All moods</option>{facets?.moods.map((value) => <option key={value}>{value}</option>)}</Select>
      </div>
      {visibleCategories.length > 0 && <div className="mt-3 flex flex-wrap items-center gap-2"><span className="mr-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-studio-faint"><Layers3 className="h-3.5 w-3.5" /> Categories</span>{visibleCategories.map((category) => <button key={category.id} type="button" aria-pressed={categoryIds.includes(category.id)} onClick={() => toggleCategory(category.id)} className={cn("min-h-10 rounded-full border px-2.5 py-1 text-[11px] transition-colors", categoryIds.includes(category.id) ? "border-studio-accent bg-studio-accent text-studio-accent-fg" : "border-studio-border text-studio-muted hover:border-studio-border-light hover:text-studio-ink")}>{category.name} <span className="ml-1 opacity-60">{category.item_count ?? 0}</span></button>)}</div>}
    </div>

    <div className="mb-3 mt-6 flex items-center justify-between"><p data-testid="library-total" className="text-xs font-medium text-studio-muted">{data?.total ?? 0} reusable assets</p>{categoryIds.length > 0 && <button className="text-xs font-medium text-studio-accent" onClick={() => setCategoryIds([])}>Clear category filters</button>}</div>
    {isLoading ? <div className="flex h-64 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-studio-muted" /></div>
      : !data?.items.length ? <EmptyState icon={BookOpen} size="md" title="No Library items match these filters" hint="Try a different scope, category, or search term" />
        : <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">{data.items.map((item) => <LibraryCard key={item.id} item={item} onOpen={() => setSelected(item)} />)}</div>}
    {(data?.total ?? 0) > 48 && <div className="mt-5 flex items-center justify-center gap-4"><Button variant="secondary" disabled={offset === 0} onClick={() => setOffset((value) => Math.max(0, value - 48))}>Previous</Button><span className="text-xs text-studio-muted">Page {Math.floor(offset / 48) + 1} of {Math.ceil((data?.total ?? 0) / 48)}</span><Button variant="secondary" disabled={offset + 48 >= (data?.total ?? 0)} onClick={() => setOffset((value) => value + 48)}>Next</Button></div>}
    <ItemDetail
      item={selected}
      categories={categories ?? []}
      onClose={() => { setSelected(null); if (relatedTechnique) { const next = new URLSearchParams(searchParams); next.delete("technique"); setSearchParams(next, { replace: true }); } }}
      onUpdated={(item) => { setSelected(item); void mutateItems(); }}
      onArchived={() => { setSelected(null); void mutateItems(); void mutateCategories(); }}
    />
    <ManageCategoriesDialog open={categoryDialog} categories={categories ?? []} onClose={() => setCategoryDialog(false)} onChanged={() => { void mutateCategories(); void mutateItems(); }} />
  </div>;
}
